import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Search, GraduationCap, Building2, Users, Presentation, Loader2, CornerDownLeft } from "lucide-react";

const TYPE_META = {
  corso: { label: "Corsi", icon: GraduationCap },
  azienda: { label: "Aziende", icon: Building2 },
  persona: { label: "Persone", icon: Users },
  docente: { label: "Docenti e tutor", icon: Presentation },
};

export default function GlobalSearch({ className = "" }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inCal = pathname.startsWith("/calendario");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/search/global", { params: { q: term } })
        .then(({ data }) => { if (active) { setResults(data.results || []); setOpen(true); } })
        .catch(() => { if (active) setResults([]); })
        .finally(() => { if (active) setLoading(false); });
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const select = (r) => {
    setOpen(false);
    const term = q.trim();
    setQ("");
    setResults([]);
    if (inCal) {
      navigate(r ? `/calendario?${r.type}=${encodeURIComponent(r.id)}` : `/calendario?q=${encodeURIComponent(term)}`);
      return;
    }
    navigate(["corso", "persona", "docente"].includes(r.type) ? r.route : `${r.route}?q=${encodeURIComponent(term)}`);
  };

  const grouped = {};
  results.forEach((r) => { (grouped[r.type] = grouped[r.type] || []).push(r); });
  const order = ["corso", "azienda", "persona", "docente"];

  return (
    <div className={`relative ${className}`} ref={ref} data-testid="global-search-wrap">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter" && results.length) select(results[0]);
          else if (e.key === "Enter" && inCal && q.trim()) select(null);
        }}
        placeholder={inCal ? "Cerca nel calendario..." : "Cerca corsi, aziende, persone..."}
        data-testid="global-search"
        className="h-10 sm:h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-8 text-sm outline-none focus:border-[#E11D48] focus:bg-white"
      />
      {loading && <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}

      {open && q.trim().length >= 2 && (
        <div className="fixed inset-x-2 top-16 z-50 max-h-[70vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl sm:absolute sm:inset-x-auto sm:left-0 sm:right-0 sm:top-11" data-testid="global-search-results">
          {results.length === 0 && !loading ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400" data-testid="global-search-empty">
              Nessun risultato per "{q.trim()}"
            </div>
          ) : (
            <div className="py-2">
              {order.filter((t) => grouped[t]).map((t) => {
                const Meta = TYPE_META[t];
                const Icon = Meta.icon;
                return (
                  <div key={t}>
                    <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{Meta.label}</div>
                    {grouped[t].map((r) => (
                      <button
                        key={r.id}
                        onClick={() => select(r)}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-rose-50/60"
                        data-testid={`search-result-${t}`}
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-slate-800">{r.label}</span>
                          {r.sublabel && <span className="block truncate text-xs text-slate-400">{r.sublabel}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })}
              <div className="mt-1 flex items-center gap-1.5 border-t border-slate-100 px-3 py-2 text-[11px] text-slate-400">
                <CornerDownLeft className="h-3 w-3" /> {inCal ? "Seleziona per filtrare il calendario" : "Invio per aprire il primo risultato"}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
